import {Component, Inject, LOCALE_ID, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ProfileService} from "../../services/profile/profile.service";
import {LANG_OPTIONS} from "../../lang.constants";
import {BsModalRef, BsModalService} from "ngx-bootstrap/modal";
import {SystemLangEditModalComponent} from "../../components/system-lang-edit-modal/system-lang-edit-modal.component";
import * as _ from 'lodash';
import {EmailEditModalComponent} from "../../components/email-edit-modal/email-edit-modal.component";
import {PasswordUpdateModalComponent} from "../../components/password-update-modal/password-update-modal.component";
import {MobileNumberModalComponent} from "../../components/mobile-number-modal/mobile-number-modal.component";
import {MobileVerificationModalComponent} from "../../components/mobile-verification-modal/mobile-verification-modal.component";
import {IsDirty} from "../../services/can-deactivate-guard/is-dirty.interface";
import {AlertBannerService, AlertType} from "../../lib/alert-banner";
import {environment} from "../../../environments/environment";
import {ConstantsService as constants, ConstantsService} from "../../services/constants/constants.service";
import * as Cookies from "js-cookie";
import {Router} from "@angular/router";
import { setPageDirtyFlag } from 'src/app/shared/page-dirty-flag';
import {PermissionsService} from "../../services/permissions/permissions.service";
import {IdService} from "../../services/id/id.service";
import {PhoneNumberUtil, PhoneNumberFormat} from "google-libphonenumber";
import PhoneInputService from "../../lib/phone-input/phone-input.service";
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.pug',
  styleUrls: ['./profile.component.scss'],
  standalone: false
})
export class ProfileComponent implements OnInit, IsDirty, OnDestroy {
  phoneUtil = PhoneNumberUtil.getInstance();
  profileData: any = {};
  avatar: any = '';
  email = '';
  systemLang;
  @ViewChild('profileForm', {static: false}) profileForm: any;
  default_logo = ConstantsService.DEFAULT_LOGO;
  messages = [];
  bsModalRef!: BsModalRef;
  showValidationError = false;
  file: any = null;
  filename = ''
  formChanges;
  photoRemoved = false;
  _formDirty = false;
  errorMap = new Map([
    [
      "password",
      new Map<any, any>([
        ["incorrect", $localize`Your current password did not match our records. Please try again.`],
        ["Password should be different from last 6 passwords", $localize`Your password must be different to your last six passwords.`]
      ])
    ]
  ]);
  loading = true;
  private destroy$ = new Subject<void>();
  ent_7705_pwd_policy: boolean = false;
  ent_10517_remove_secondary_email: boolean = false;
  ent_10657_mobile_verification: boolean = false;

  // Tracks when resend code was last clicked (persists across modal instances)
  private lastResendTimestamp: number | null = null;

  get formDirty() { return this._formDirty }
  set formDirty(value) {
    this._formDirty = value;
    setPageDirtyFlag(value);
  }

  // Flag for displaying mobile number verification state
  get mobileNeedsVerification(): boolean {
    const hasMobile = !!this.profileData?.mobile;
    const verified = this.profileData?.mobile_verified;

    // 1. Ph# exists + verified = Update
    // 2. Ph# exists + not verified = Verification required
    // 3. Ph# not exists = Verification required
    if (hasMobile && verified) {
      return false;
    }
    return true;
  }

  // Label for the mobile number action link button
  get mobileActionLabel(): string {
    if (this.mobileNeedsVerification) {
      return $localize`Verification required`;
    }
    const mobile = this.profileData?.mobile;
    if (mobile) {
      return this.parsePhoneNumber(mobile).formatted;
    }
    return $localize`Verification required`;
  }

  /**
   * Parses a phone number string and returns country code, phone number, and formatted display.
   */
  private parsePhoneNumber(phone: string): { countryCode: string; phoneNumber: string; formatted: string } {
    try {
      const normalized = phone.startsWith('+') ? phone : '+' + phone;
      const parsedPhone = this.phoneUtil.parse(normalized);
      const countryCode = '' + parsedPhone.getCountryCode();
      const phoneNumber = '' + parsedPhone.getNationalNumber();
      const formatted = this.phoneUtil.format(parsedPhone, PhoneNumberFormat.INTERNATIONAL);
      return { countryCode, phoneNumber, formatted };
    } catch (e) {
      // Fallback using PhoneInputService
      const { callingCode, phoneNumber } = PhoneInputService.setCountryCode(phone);
      const countryCode = (callingCode || '').replace(/^\+/, '');
      const normalized = phone.startsWith('+') ? phone : '+' + phone;
      const formatted = normalized.replace(/^(\+\d{1,3})(\d+)$/, '$1 $2');
      return { countryCode, phoneNumber: phoneNumber || '', formatted };
    }
  }

  constructor(
    private profile: ProfileService,
    private id: IdService,
    private modalService: BsModalService,
    private alertList: AlertBannerService,
    private router: Router,
    private permissions: PermissionsService,
    @Inject(LOCALE_ID) public locale: string,
  ) {
  }

  ngOnInit(): void {
    this.ent_7705_pwd_policy = this.permissions.isFeatureActivated('ENT_7705_SIGNUP_PASSWORD_POLICY');
    this.ent_10517_remove_secondary_email = this.permissions.isFeatureActivated('ent-10517-remove-secondary-email-address');
    this.ent_10657_mobile_verification = this.permissions.isFeatureActivated('ent-10657-mobile-verification');
    this.getProfileDetails();
    setPageDirtyFlag(false);
    if(this.ent_7705_pwd_policy) {
      this.errorMap.get("password")?.set("Current password is incorrect", $localize`Current password is incorrect.`)
    }
  }

  onManageMobile() {
    const currentMobile = this.profileData?.mobile || '';
    const isVerified = this.profileData?.mobile_verified || false;
    const initialState = { phone: currentMobile, isVerified };
    const mobileModalRef = this.modalService.show(MobileNumberModalComponent, {
      class: 'modal-dialog modal-dialog-centered m-popup padding-0',
      initialState,
      ignoreBackdropClick: true
    });

    const mobileModalComponent = mobileModalRef.content as MobileNumberModalComponent;

    mobileModalComponent.event.pipe(takeUntil(this.destroy$)).subscribe(result => {
      const newMobile = result?.phone;

      // If mobile is cleared, save empty mobile directly (no verification needed).
      // Handle edge cases: null, empty string, just country code prefix, or malformed "+1null"
      if (this.isEmptyMobile(newMobile)) {
        this.clearMobileNumber(mobileModalComponent);
        return;
      }

      // If verified and the number didn't change, just close.
      // For unverified numbers, always show verification modal even without changes.
      if (isVerified && newMobile === currentMobile) {
        mobileModalComponent.closeModal();
        return;
      }

      // Open verification modal for:
      // 1. Unverified numbers (even without changes - user needs to verify)
      // 2. Changed numbers (user needs to verify the new number)
      this.showVerificationModal(newMobile, mobileModalComponent);
    });
  }

  /**
   * Clears the mobile number by calling BE endpoint (no verification needed).
   */
  private clearMobileNumber(mobileModalComponent: MobileNumberModalComponent) {
    mobileModalComponent.isLoading = true;
    this.profile.triggerMobileVerification('', '').pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        mobileModalComponent.isLoading = false;
        if (response) {
          this.profileData.mobile = response.mobile;
          this.profileData.mobile_verified = response.mobile_verified;
        }
        mobileModalComponent.closeModal();
      },
      error: () => {
        mobileModalComponent.isLoading = false;
        this.alertList.append([{
          type: AlertType.DANGER,
          message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
          keepOnRouteChange: true
        }]);
      }
    });
  }

  /**
   * Opens the verification modal on top of the mobile number modal.
   * Handles verification success/cancel scenarios.
   */
  private showVerificationModal(newMobile: string, mobileModalComponent: MobileNumberModalComponent) {
    const { countryCode, phoneNumber } = this.parsePhoneNumber(newMobile);

    // Trigger SMS verification before showing the modal
    mobileModalComponent.isLoading = true;
    this.profile.triggerMobileVerification(countryCode, phoneNumber).subscribe({
      next: (response) => {
        mobileModalComponent.isLoading = false;
        // Update profile data from response to keep UI in sync
        if (response) {
          this.profileData.mobile = response.mobile;
          this.profileData.mobile_verified = response.mobile_verified;
        }
        this.openVerificationModalWithData(countryCode, phoneNumber, mobileModalComponent);
      },
      error: () => {
        mobileModalComponent.isLoading = false;
        this.alertList.append([{
          type: AlertType.DANGER,
          message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
          keepOnRouteChange: true
        }]);
      }
    });
  }

  /**
   * Opens the verification modal after trigger API succeeds.
   */
  private openVerificationModalWithData(
    countryCode: string,
    phoneNumber: string,
    mobileModalComponent: MobileNumberModalComponent
  ) {
    const initialState = { countryCode, phoneNumber, lastResendTimestamp: this.lastResendTimestamp };
    const verificationRef = this.modalService.show(MobileVerificationModalComponent, {
      class: 'modal-dialog modal-dialog-centered s-popup padding-0',
      initialState,
      ignoreBackdropClick: true
    });

    const verificationComponent = verificationRef.content as MobileVerificationModalComponent;

    // Listen for resend events to persist cooldown
    verificationComponent.resendEvent.pipe(takeUntil(this.destroy$)).subscribe((timestamp: number) => {
      this.lastResendTimestamp = timestamp;
    });

    verificationComponent.event.pipe(takeUntil(this.destroy$)).subscribe(verificationResult => {
      if (verificationResult?.verified) {
        // Verification succeeded: update profile data from response and close both modals.
        this.profileData.mobile = verificationResult.mobile;
        this.profileData.mobile_verified = verificationResult.mobile_verified;
        mobileModalComponent.closeModal();
        // Reset cooldown on successful verification
        this.lastResendTimestamp = null;
      }
      // If cancelled: verification modal closes, mobile modal stays open with edits intact.
      // User can modify and try again.
    });
  }

  /**
   * Checks if a mobile number value represents an empty/cleared state.
   * Handles edge cases from phone input: null, empty, just country code, or malformed values.
   */
  private isEmptyMobile(value: string | null | undefined): boolean {
    if (!value) {
      return true;
    }
    // Remove + prefix and check if only digits remain
    const digitsOnly = value.replace(/\D/g, '');
    // If 3 or fewer digits, it's likely just a country code (e.g., "+1", "+61", "+354")
    if (digitsOnly.length <= 3) {
      return true;
    }
    // Handle malformed values like "+1null"
    if (value.toLowerCase().includes('null') || value.toLowerCase().includes('undefined')) {
      return true;
    }
    return false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.formChanges?.unsubscribe();
    setPageDirtyFlag(false);
  }

  redirectOnLanguageBasedOnLocale(localeId: string) {
    let updatedProfile = {'preferred_language': localeId};
    this.loading = true;
    this.profile.updateProfile(updatedProfile).subscribe(data => {
      this.loading = false;
      if (data['saved']) {
        this.profileData.preferred_language = localeId;
        this.redirectToNewLang(this.profileData.preferred_language);
      }
    }, error => {
      this.loading = false;
      console.log(error);
      this.alertList.append([{
        type: AlertType.DANGER,
        message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
        keepOnRouteChange: true
      }]);
    });
  }

  getProfileDetails(): any {
    this.profile.getProfile().subscribe(data => {
      this.profileData = data;
      if (this.profileData?.preferred_language != this.locale.toLowerCase()) {
        this.redirectOnLanguageBasedOnLocale(this.profileData?.preferred_language);
      }
      this.loading = false;
      this.avatar = this.profileData['avatar'] === null ? this.default_logo : this.profileData['avatar'];
      this.setPrimaryEmail();
      this.setSystemLang();
      this.formChanges = this.profileForm.form.valueChanges.subscribe((changes) => {
        if (this.profileForm.form.dirty) {
          setPageDirtyFlag(true);
        }
      });
    }, error => {
      this.loading = false;
      console.log(error);
      this.alertList.append([{
        type: AlertType.DANGER,
        message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
        keepOnRouteChange: true
      }]);
    });
  }

  setPrimaryEmail() {
    if(this.ent_10517_remove_secondary_email){
      if (this.profileData['email']) {
        this.email = this.profileData['email'];
      }
    }
    else {
      let email = this.profileData['email'].find(email => email['is_primary'])
      if (email !== undefined) {
        this.email = email.email_id;
      } else if (this.profileData['email'].length > 0) {
        this.email = this.profileData['email'][0].email_id;
      } else {
        this.email = 'Update';
      }
    }
  }

  setSystemLang() {
    this.systemLang = LANG_OPTIONS.get(this.profileData['preferred_language']);
    if (this.systemLang === undefined) {
      this.systemLang = 'Update';
    }
  }

  save() {
    this.messages = [];
    this.showValidationError = true;
    if (!this.profileForm.valid) {
      return;
    }
    let toUpdate = new FormData();
    if (this.file !== null) {
      toUpdate.append('file', this.file, this.filename);
    } else if (this.photoRemoved) {
      toUpdate.append('avatar', '');
    }
    toUpdate.append('first_name', this.profileData.first_name);
    toUpdate.append('last_name', this.profileData.last_name);
    if (!this.ent_10657_mobile_verification) {
      toUpdate.append('mobile', this.profileData.mobile);
    }
    toUpdate.append('job_title', this.profileData.job_title);
    toUpdate.append('first_name', this.profileData.first_name);
    this.loading = true;
    this.profile.updateProfile(toUpdate).subscribe(data => {
      this.loading = false;
      if (data['saved']) {
        this.setMessages([{
          'type': 'success',
          'message': $localize`Profile has been updated`
        }]);
        if (this.file !== null) {
          this.profileData.avatar = this.file;
          this.file = null;
          this.filename = '';
        }
        this.markAsPristine();
      } else { // Error case
        console.log(data);
        this.setMessages([{
          'type': 'danger',
          'message': $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`
        }]);
      }
    }, error => {
      this.loading = false;
      console.log(error);
      let message = $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`;
      if (error?.error?.data?.errors?.avatar === 'INFECTED_FILE_ERROR') {
        message = $localize`Virus detected. Please upload a virus-free file and try again`;
      }
      this.alertList.append([{
        type: AlertType.DANGER,
        message: message,
        keepOnRouteChange: true
      }]);
    });
  }

  removePhoto() {
    this.avatar = this.default_logo;
    this.file = null;
    this.formDirty = true;
    this.photoRemoved = true;
  }

  showEditLangModal() {
    let initialState = {'preferred_language': this.profileData['preferred_language']};
    this.bsModalRef = this.modalService.show(SystemLangEditModalComponent, {
      class: 'modal-dialog-centered max-width-400', initialState
    });
    this.bsModalRef.content.event.subscribe(updateLang => {
      this.switchLang(updateLang);
    });
  }

  switchLang(newLang: any) {
    let updatedProfile = {'preferred_language': newLang['newLang']};
    this.loading = true;
    this.profile.updateProfile(updatedProfile).subscribe(data => {
      this.loading = false;
      if (data['saved']) {
        this.profileData['preferred_language'] = newLang['newLang'];
        this.bsModalRef.content.markAsPristine();
        this.bsModalRef.hide();
        this.redirectToNewLang(this.profileData['preferred_language']);
      } else { // Error case
        console.log(data);
        this.bsModalRef.content.setMessages(
          [
            {
              'type': 'danger',
              'message': $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`
            }
          ]
        );
      }
    }, error => {
      this.loading = false;
      console.log(error);
      this.alertList.append([{
        type: AlertType.DANGER,
        message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
        keepOnRouteChange: true
      }]);
    });
  }

  redirectToNewLang(newLang: any) {
    Cookies.set(constants.XREF_LOCALE, newLang, {domain: environment.COOKIE_DOMAIN});
    window.open(environment.DOMAIN + ConstantsService.LANG_LOCALE_MAP.get(newLang) + this.router.url, "_self");
  }

  // when cleaning up the FF ent-10517-remove-secondary-email-address,
  // we should comment out the following function showEditEmailModal()
  showEditEmailModal() {
    let initialState = {'emails': _.cloneDeep(this.profileData['email'])};
    this.bsModalRef = this.modalService.show(EmailEditModalComponent, {
      class: 'modal-dialog-centered modal-dialog-scrollable modal-lg', initialState
    });
    this.bsModalRef.content.event.subscribe(emails => {
      this.profileData['email'] = emails;
      this.setPrimaryEmail();
    });
  }

  showUpdatePasswordModal() {
    if (this.ent_7705_pwd_policy) {
      this.bsModalRef = this.modalService.show(PasswordUpdateModalComponent, {
        class: 'modal-dialog-centered password-modal-dialog'
      });
    } else {
      this.bsModalRef = this.modalService.show(PasswordUpdateModalComponent, {
        class: 'modal-dialog-centered max-width-400'
      });
    }
    this.bsModalRef.content.event.subscribe(passwords => {
      this.updatePassword(passwords);
    });
  }

  updatePassword(passwords) {
    this.loading = true;
      this.id.updatePassword(passwords).subscribe(data => {
        this.loading = false;
        if (data['saved']) {
          this.alertList.append([{
            type: AlertType.SUCCESS,
            message: $localize`Your new password was saved successfully.`,
            keepOnRouteChange: true
          }]);
          this.bsModalRef.content.markAsPristine();
          this.bsModalRef.hide();
        }
        else { // Error case
          console.log(data);
          this.bsModalRef.content.setMessages(
            [
              {
                'type': 'danger',
                'message': $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`
              }
            ]
          );
        }
      }, error => {
        this.loading = false;
        console.log(error);
        let messages: Array<any> = [];
        if (this.ent_7705_pwd_policy) {
          const nonFieldErrors = error?.error?.message?.non_field_errors;
          if (nonFieldErrors) {
            nonFieldErrors.forEach((item: string) => {
              const mappedMessage = this.errorMap.get("password")?.get(item) || item;
              if (mappedMessage === this.errorMap.get("password")?.get('Password should be different from last 6 passwords')) {
                this.bsModalRef.content.isPasswordReused = mappedMessage;
                return;
              } else if (mappedMessage === this.errorMap.get("password")?.get('Current password is incorrect')) {
                this.bsModalRef.content.isCurrentPasswordCorrect = mappedMessage;
                return;
              }
              messages.push({
                type: 'danger',
                message: mappedMessage
              });
            });
          } else {
            messages.push({
              type: 'danger',
              message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`
            });
          }
        } else {
          for (const [key, value] of Object.entries(error.error['data']['errors'])) {
            let key_map = this.errorMap.has(key) ? this.errorMap.get(key) : new Map();
            let message: string = key_map?.has(value) ? key_map?.get(value) : $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`
            messages.push({
              'type': 'danger',
              'message': message
            });
          }
        }
        this.bsModalRef.content.setMessages(messages);
      });
  }

  isDirty(): boolean {
    return this.formDirty || this.profileForm.dirty ||
      (this.bsModalRef && 'isDirty' in this.bsModalRef.content && this.bsModalRef.content.isDirty());
  }

  setMessages(newMessages) {
    this.messages = this.messages.concat(newMessages);
  }

  markAsPristine() {
    this.profileForm.form.markAsPristine();
    this.formDirty = false;
  }

  handleFileInput(event) {
    if (event.target.files && event.target.files.length) {
      const [file] = event.target.files;
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        this.imageUpdate({'file': file, 'avatar': reader.result as string, 'filename': file.name});
      };
    }
  }

  imageUpdate(event: any) {
    this.file = event['file'];
    this.avatar = event['avatar'];
    this.filename = event['filename'];
    this.formDirty = true;
    this.photoRemoved = false;
  }

  phoneUpdate(event) {
    this.profileData['mobile'] = event['phone'];
    this.formDirty = true;
  }
}
